/**
 * Work Package Drawer - Professional Editor
 *
 * Modern, Notion-like editor for templates and work items.
 *
 * Design principles:
 * - 80% viewport width for comfortable editing
 * - Always in edit mode - no view/edit toggle
 * - Auto-save with visual status indicator
 * - Clean, spacious layout
 * - Keyboard shortcuts (Ctrl+S save, Escape close)
 */

import { formatUrl } from '@/utils/url';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// ... (in formContent)



import {
  Drawer,
  Modal,
  Form,
  Input,
  Button,
  Space,
  DatePicker,
  Select,
  message,
  Spin,
  Popconfirm,
  InputNumber,
  Switch,
  Tooltip,
  Tag,
  Divider,
  Upload,
  Collapse,
  type UploadFile,
} from 'antd';

import {
  DeleteOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloudOutlined,
  CloseOutlined,
  CalendarOutlined,
  UserOutlined,
  TagOutlined,
  FolderOutlined,
  BellOutlined,
  InboxOutlined,
  LinkOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  HistoryOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { templatesApi, instancesApi } from '@api/index';
import { filemanagerApi } from '@api/filemanager';
import apiClient from '@api/client';
import { CategorySelector, TagSelector, UserSelector, BusinessDomainSelector } from '@components/selectors';
import {
  RichTextEditor,
  textToYooptaContent,
  yooptaToText,
  descriptionToYooptaContent,
  yooptaContentToHtml,
  createYooptaEditor,
  type YooptaContentValue,
} from '@components/notes/RichTextEditor';
import { InstanceSubtasksMiniGrid, TemplateSubtasksMiniGrid } from '@components/subtasks';
import { ActivityTab } from '@components/activity';
import { FileList } from '@components/files';
import { AIQuickActions, AIChatPanel } from '@components/ai';
import VersionHistoryDrawer from '@components/common/VersionHistoryDrawer';
import type { RecurrenceRule, IntervalUnit, ProcessTemplate, ProcessInstance } from '@/types';

// Typography Text replaced by inline div/span elements for Blueprint styling

interface WorkPackageDrawerProps {
  open: boolean;
  itemId?: string;
  mode: 'view' | 'edit' | 'create';
  isTemplate?: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const intervalUnits: { value: IntervalUnit; label: string }[] = [
  { value: 'day', label: 'Days' },
  { value: 'week', label: 'Weeks' },
  { value: 'month', label: 'Months' },
  { value: 'year', label: 'Years' },
];

// Debounce hook for auto-save
function useDebounce<T extends (...args: any[]) => any>(callback: T, delay: number): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    }) as T,
    [delay]
  );
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export default function WorkPackageDrawer({
  open,
  itemId,
  mode,
  isTemplate: initialIsTemplate = false,
  onClose,
  onSaved,
}: WorkPackageDrawerProps) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const titleInputRef = useRef<any>(null);
  const initialLoadRef = useRef(true);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [descriptionContent, setDescriptionContent] = useState<YooptaContentValue>(textToYooptaContent(''));
  const [descriptionText, setDescriptionText] = useState('');
  const [descriptionHtml, setDescriptionHtml] = useState('');
  // Holds the Yoopta editor instance once RichTextEditor mounts
  const editorInstanceRef = useRef<ReturnType<typeof createYooptaEditor> | null>(null);
  // Pending description string to load once editor is ready
  const pendingDescriptionRef = useRef<string | null>(null);
  const [isRecurringTemplate, setIsRecurringTemplate] = useState(initialIsTemplate);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [hasChanges, setHasChanges] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [showChat, setShowChat] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Fetch files for this entity
  const { data: serverFiles, refetch: refetchFiles } = useQuery({
    queryKey: ['files', itemId],
    queryFn: () => filemanagerApi.listFiles('', undefined, itemId),
    enabled: !!itemId && mode !== 'create',
    refetchOnWindowFocus: false,
  });

  // Sync server files to fileList
  useEffect(() => {
    if (serverFiles) {
      setFileList(prev => {
        // Keep files that are currently uploading (not 'done' yet)
        const uploading = prev.filter(f => f.status === 'uploading');

        const loaded: UploadFile[] = serverFiles
          .filter(f => f.type === 'file')
          .map(f => ({
            uid: f.id,
            name: f.name,
            status: 'done',
            url: f.url,
            thumbUrl: f.purl,
            size: f.size,
          }));

        return [...uploading, ...loaded];
      });
    }
  }, [serverFiles]);

  // Called once when Yoopta editor mounts — resolve any pending HTML description
  const handleEditorReady = useCallback((editor: ReturnType<typeof createYooptaEditor>) => {
    editorInstanceRef.current = editor;
    if (pendingDescriptionRef.current !== null) {
      const content = descriptionToYooptaContent(pendingDescriptionRef.current, editor);
      setDescriptionContent(content);
      const text = yooptaToText(content, true);
      setDescriptionText(text);
      setDescriptionHtml(yooptaContentToHtml(content, editor));
      pendingDescriptionRef.current = null;
    }
  }, []);

  // Load a description string into the editor, handling HTML/markdown/plain text
  const loadDescription = useCallback((raw: string) => {
    if (editorInstanceRef.current) {
      const content = descriptionToYooptaContent(raw, editorInstanceRef.current);
      setDescriptionContent(content);
      setDescriptionText(yooptaToText(content, true));
      setDescriptionHtml(yooptaContentToHtml(content, editorInstanceRef.current));
    } else {
      // Editor not ready yet — queue it
      pendingDescriptionRef.current = raw;
      setDescriptionContent(textToYooptaContent(''));
      setDescriptionText('');
      setDescriptionHtml('');
    }
  }, []);

  // Auto-focus title on open
  useEffect(() => {
    if (open && titleInputRef.current) {
      setTimeout(() => titleInputRef.current?.focus(), 150);
    }
  }, [open]);

  // Fetch template or instance
  const { data: item, isLoading } = useQuery<ProcessTemplate | ProcessInstance>({
    queryKey: [isRecurringTemplate || initialIsTemplate ? 'template' : 'instance', itemId],
    queryFn: async () => {
      if (isRecurringTemplate || initialIsTemplate) {
        return templatesApi.get(itemId!) as Promise<ProcessTemplate>;
      }
      return instancesApi.get(itemId!) as Promise<ProcessInstance>;
    },
    enabled: !!itemId && open,
  });

  // Reset form when item/mode changes
  useEffect(() => {
    if (!open) return;
    initialLoadRef.current = true;

    if (item) {
      const isTemplate = (i: ProcessTemplate | ProcessInstance): i is ProcessTemplate => 'recurrenceRule' in i;

      if (isTemplate(item)) {
        loadDescription(item.description || '');
        setIsRecurringTemplate(true);
        const rule = item.recurrenceRule;
        form.setFieldsValue({
          title: item.title,
          categoryId: item.categoryId,
          tagIds: item.tags?.map((t) => t.id) || [],
            defaultAssigneeId: item.defaultAssigneeId,
          defaultAssigneeIds: item.defaultAssigneeIds || (item.defaultAssigneeId ? [item.defaultAssigneeId] : []),
          domainIds: item.domainIds || (item.domainId ? [item.domainId] : []),
          notificationDaysBefore: item.notificationDaysBefore,
          isActive: item.isActive,
          every: rule.type === 'interval' ? rule.every : 1,
          unit: rule.type === 'interval' ? rule.unit : 'month',
          startDate: rule.type === 'once' ? dayjs(rule.date) : dayjs(rule.startDate),
        });
      } else {
        const instance = item as ProcessInstance;
        // Prefer instance-level description, fall back to template description
        const desc = (instance as any).description || instance.template?.description || '';
        loadDescription(desc);
        setIsRecurringTemplate(false);
        form.setFieldsValue({
          title: instance.title || instance.template?.title || '',
          categoryId: instance.categoryId || instance.template?.categoryId,
          tagIds: instance.tags?.map((t) => t.id) || instance.template?.tags?.map((t) => t.id) || [],
          assignedUserIds: (instance as any).assignedUsers?.map((u: any) => u.id)
            || (instance.assignedUserId ? [instance.assignedUserId] : []),
          dueDate: dayjs(instance.dueDate),
          status: instance.status,
        });
      }
    } else if (mode === 'create') {
      form.resetFields();
      loadDescription('');
      setIsRecurringTemplate(initialIsTemplate);
      form.setFieldsValue({
        isActive: true,
        every: 1,
        unit: 'month',
        startDate: dayjs(),
        dueDate: dayjs(),
        status: 'pending',
        notificationDaysBefore: [7],
      });
    }

    setTimeout(() => {
      initialLoadRef.current = false;
    }, 200);
  }, [item, form, mode, open, initialIsTemplate]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      initialLoadRef.current = true;
      setHasChanges(false);
      setSaveStatus('idle');
    }
  }, [open]);

  // Build recurrence rule
  const buildRecurrenceRule = useCallback((values: any): RecurrenceRule => {
    const startDate = values.startDate?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD');
    return {
      type: 'interval',
      every: values.every || 1,
      unit: values.unit || 'month',
      startDate,
    };
  }, []);

  // Mutations
  const createTemplateMutation = useMutation({
    mutationFn: (data: any) => templatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setSaveStatus('saved');
      setHasChanges(false);
      onSaved?.();
      onClose();
    },
    onError: () => {
      setSaveStatus('error');
      message.error('Failed to create');
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: (data: any) => templatesApi.update(itemId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template', itemId] });
      setSaveStatus('saved');
      setHasChanges(false);
      onSaved?.();
    },
    onError: () => {
      setSaveStatus('error');
      message.error('Failed to save');
    },
  });

  const createInstanceMutation = useMutation({
    mutationFn: (data: any) => instancesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      setSaveStatus('saved');
      setHasChanges(false);
      onSaved?.();
      onClose();
    },
    onError: () => {
      setSaveStatus('error');
      message.error('Failed to create');
    },
  });

  const updateInstanceMutation = useMutation({
    mutationFn: (data: any) => instancesApi.update(itemId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      queryClient.invalidateQueries({ queryKey: ['instance', itemId] });
      setSaveStatus('saved');
      setHasChanges(false);
      onSaved?.();
    },
    onError: () => {
      setSaveStatus('error');
      message.error('Failed to save');
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: () => templatesApi.delete(itemId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      message.success('Deleted');
      onClose();
    },
    onError: () => message.error('Failed to delete'),
  });

  const deleteInstanceMutation = useMutation({
    mutationFn: () => instancesApi.delete(itemId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      message.success('Deleted');
      onClose();
    },
    onError: () => message.error('Failed to delete'),
  });

  // Auto-save function
  const performSave = useCallback(async (currentDescription: string) => {
    try {
      const values = form.getFieldsValue();
      if (!values.title?.trim()) return;

      setSaveStatus('saving');

      // Use HTML if available (currentDescription may be HTML or plain text)
      const descriptionToSave = currentDescription || null;

      const baseData = {
        title: values.title,
        description: descriptionToSave,
        categoryId: values.categoryId || null,
        tagIds: values.tagIds || [],
        domain_ids: values.domainIds || [],
      };

      if (isRecurringTemplate) {
        const recurrenceRule = buildRecurrenceRule(values);
        const templateData = {
          ...baseData,
          recurrenceRule,
          default_assignee_ids: values.defaultAssigneeIds || [],
          defaultAssigneeId: values.defaultAssigneeId || null,
          notificationDaysBefore: values.notificationDaysBefore || [],
          isActive: values.isActive ?? true,
        };

        if (mode === 'create' || !itemId) {
          createTemplateMutation.mutate(templateData);
        } else {
          updateTemplateMutation.mutate(templateData);
        }
      } else {
        const instanceData = {
          ...baseData,
          dueDate: values.dueDate?.format('YYYY-MM-DD'),
          assignedUserId: (values.assignedUserIds || [])[0] || null,
          assignedUserIds: values.assignedUserIds || [],
          status: values.status || 'pending',
        };

        if (mode === 'create' || !itemId) {
          createInstanceMutation.mutate(instanceData);
        } else {
          updateInstanceMutation.mutate(instanceData);
        }
      }
    } catch {
      setSaveStatus('error');
    }
  }, [form, mode, isRecurringTemplate, buildRecurrenceRule, createTemplateMutation, updateTemplateMutation, createInstanceMutation, updateInstanceMutation, itemId]);

  // Debounced auto-save (1.5 second delay)
  const debouncedSave = useDebounce(performSave, 1500);

  // Handle form field changes
  const handleFieldChange = useCallback(() => {
    if (initialLoadRef.current) return;
    setHasChanges(true);
    setSaveStatus('idle');
    if (mode !== 'create') {
      debouncedSave(descriptionHtml || descriptionText);
    }
  }, [mode, debouncedSave, descriptionHtml, descriptionText]);

  // Handle description changes
  const handleDescriptionChange = useCallback((content: YooptaContentValue) => {
    setDescriptionContent(content);
    const newText = yooptaToText(content, true);
    setDescriptionText(newText);
    const newHtml = yooptaContentToHtml(content, editorInstanceRef.current);
    setDescriptionHtml(newHtml);
    if (initialLoadRef.current) return;
    setHasChanges(true);
    setSaveStatus('idle');
    if (mode !== 'create') {
      debouncedSave(newHtml || newText);
    }
  }, [mode, debouncedSave]);

  // Manual create
  const handleCreate = useCallback(() => {
    performSave(descriptionHtml || descriptionText);
  }, [performSave, descriptionHtml, descriptionText]);

  const isSaving =
    createTemplateMutation.isPending ||
    updateTemplateMutation.isPending ||
    createInstanceMutation.isPending ||
    updateInstanceMutation.isPending;

  // Status indicator
  const statusIndicator = useMemo(() => {
    if (mode === 'create') return null;
    if (isSaving || saveStatus === 'saving') {
      return <Tag icon={<SyncOutlined spin />} color="processing">Saving...</Tag>;
    }
    if (saveStatus === 'saved' && !hasChanges) {
      return <Tag icon={<CheckCircleOutlined />} color="success">Saved</Tag>;
    }
    if (saveStatus === 'error') {
      return <Tag color="error">Error</Tag>;
    }
    if (hasChanges) {
      return <Tag icon={<CloudOutlined />}>Unsaved</Tag>;
    }
    return <Tag icon={<CheckCircleOutlined />}>Up to date</Tag>;
  }, [mode, isSaving, saveStatus, hasChanges]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (mode === 'create') {
          handleCreate();
        } else {
          performSave(descriptionHtml || descriptionText);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, mode, handleCreate, performSave, descriptionHtml, descriptionText]);

  // Main content
  const formContent = isLoading ? (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <Spin size="large" />
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0',
        borderBottom: '1px solid var(--border-default, #e4e7ec)',
        marginBottom: 16,
        flexShrink: 0,
      }}>
        <Space>
          {statusIndicator}
          {/* Business Domain Selector replacing purple badge */}
          {/* We need to import BusinessDomainSelector first, assume it's available or I will add it to imports later. 
              Actually, I should verify imports. I'll stick to removing the badge first and adding the selector in the form below or here. 
              User said "where the recurring template is we have a selector there". So here.
          */}
        </Space>
        <Space>
          <Tooltip title={isFullScreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}>
            <Button
              type="text"
              icon={isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setIsFullScreen(!isFullScreen)}
            />
          </Tooltip>
          <Tooltip title="Close">
            <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
          </Tooltip>
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        onValuesChange={handleFieldChange}
      >
        {/* Business Domain at top (sticky behavior will depend on the selector component, but placing it here) */}
        {/* User said "At the top. So where the recurring template is". 
            I'll put it in the Form, or in the Header? 
            If I put it in the Header, it's outside the Form context unless I handle it manually. 
            Better to put it at the very top of the Form.
        */}
        <Form.Item name="domainIds" style={{ marginBottom: 16 }}>
          <BusinessDomainSelector
            mode="multiple"
            placeholder="Select business domains..."
          />
        </Form.Item>

        {/* Title */}
        <Form.Item name="title" rules={[{ required: true, message: '' }]} style={{ marginBottom: 16, flexShrink: 0 }}>
          <Input
            ref={titleInputRef}
            placeholder="Untitled"
            variant="borderless"
            style={{
              fontSize: 28,
              fontWeight: 700,
              padding: 0,
              lineHeight: 1.2,
              fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
              color: 'var(--text-primary, #111827)',
            }}
          />
        </Form.Item>

        {/* Removed "Make this a recurring template" checkbox */}

        {/* Metadata grid - always visible */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          marginBottom: 20,
          flexShrink: 0,
          padding: 16,
          background: 'var(--surface-1, #fafafa)',
          border: '1px solid var(--border-subtle, #f0f2f5)',
          borderRadius: 2,
        }}>
          {/* Category */}
          <div>
            <div style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
              <FolderOutlined /> Category
            </div>
            <Form.Item name="categoryId" noStyle>
              <CategorySelector placeholder="Select category..." />
            </Form.Item>
          </div>

          {/* Tags */}
          <div>
            <div style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
              <TagOutlined /> Tags
            </div>
            <Form.Item name="tagIds" noStyle>
              <TagSelector placeholder="Add tags..." />
            </Form.Item>
          </div>

          {/* Assignee(s) — always multi for instances and templates */}
          <div>
            <div style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
              <UserOutlined /> {isRecurringTemplate ? 'Default Assignees' : 'Assignees'}
            </div>
            <Form.Item name={isRecurringTemplate ? 'defaultAssigneeIds' : 'assignedUserIds'} noStyle>
              <UserSelector multiple placeholder="Assign to..." />
            </Form.Item>
          </div>

          {/* Schedule (template) or Due Date (instance) */}
          {isRecurringTemplate ? (
            <div>
              <div style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <CalendarOutlined /> Repeat Every
              </div>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="every" noStyle>
                  <InputNumber min={1} max={365} style={{ width: '30%' }} placeholder="#" />
                </Form.Item>
                <Form.Item name="unit" noStyle>
                  <Select style={{ width: '70%' }} options={intervalUnits} />
                </Form.Item>
              </Space.Compact>
            </div>
          ) : (
            <Form.Item name="dueDate" style={{ marginBottom: 0 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                  <CalendarOutlined /> Due Date
                </div>
                <DatePicker format="YYYY-MM-DD" style={{ width: '100%', borderRadius: 2 }} className="bp-mono" />
              </div>
            </Form.Item>
          )}

          {/* Start Date (template) or Status (instance) */}
          {isRecurringTemplate ? (
            <Form.Item name="startDate" style={{ marginBottom: 0 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                  <CalendarOutlined /> Start Date
                </div>
                <DatePicker format="YYYY-MM-DD" style={{ width: '100%', borderRadius: 2 }} className="bp-mono" />
              </div>
            </Form.Item>
          ) : (
            <Form.Item name="status" style={{ marginBottom: 0 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)', marginBottom: 4 }}>
                  Status
                </div>
                <Select
                  style={{ width: '100%' }}
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'in_progress', label: 'In Progress' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'postponed', label: 'Postponed' },
                  ]}
                />
              </div>
            </Form.Item>
          )}

          {/* Reminders (template only) */}
          {isRecurringTemplate && (
            <Form.Item name="notificationDaysBefore" style={{ marginBottom: 0 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                  <BellOutlined /> Remind Before
                </div>
                <Select
                  mode="multiple"
                  placeholder="Select..."
                  style={{ width: '100%' }}
                  maxTagCount={2}
                  options={[
                    { value: 1, label: '1 day' },
                    { value: 3, label: '3 days' },
                    { value: 7, label: '1 week' },
                    { value: 14, label: '2 weeks' },
                  ]}
                />
              </div>
            </Form.Item>
          )}

          {/* External Documentation Link */}
          <Divider style={{ margin: '16px 0', borderColor: 'var(--border-subtle, #f0f2f5)' }} />
          <Form.Item
            name="outlineDocUrl"
            label="External Documentation"
            tooltip="Link to process documentation in Google Docs, Notion, Confluence, etc."
            style={{ marginBottom: 0 }}
          >
            <Input
              prefix={<LinkOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="https://docs.google.com/..."
              allowClear
              onChange={() => {
                if (mode !== 'create') {
                  // Trigger save on change if needed, or rely on auto-save
                }
              }}
            />
          </Form.Item>
          <Form.Item shouldUpdate={(prev, curr) => prev.outlineDocUrl !== curr.outlineDocUrl} noStyle>
            {({ getFieldValue }) => {
              const url = getFieldValue('outlineDocUrl');
              return url ? (
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <LinkOutlined />
                    {formatUrl(url)}
                  </a>
                </div>
              ) : null;
            }}
          </Form.Item>

        </div>

        {/* Active toggle for templates */}
        {isRecurringTemplate && (
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <Form.Item name="isActive" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
            <span style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)' }}>Active</span>
            <span style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, color: 'var(--text-tertiary, #9ca3af)' }}>
              — creates new instances automatically
            </span>
          </div>
        )}

        {/* Collapsible sections */}
        {itemId && mode !== 'create' && (
          <Collapse
            defaultActiveKey={['subtasks', 'notes']}
            ghost
            style={{ marginTop: 8 }}
            items={[
              {
                key: 'subtasks',
                label: (
                  <Space>
                    <UnorderedListOutlined />
                    <span>Subtasks</span>
                  </Space>
                ),
                children: (
                  <div style={{ marginBottom: 8 }}>
                    {isRecurringTemplate ? (
                      <TemplateSubtasksMiniGrid templateId={itemId} maxHeight={200} />
                    ) : (
                      <InstanceSubtasksMiniGrid instanceId={itemId} maxHeight={200} />
                    )}
                  </div>
                ),
              },
              {
                key: 'activity',
                label: (
                  <Space>
                    <HistoryOutlined />
                    <span>Activity Notes</span>
                  </Space>
                ),
                children: (
                  <div style={{ marginBottom: 8 }}>
                    <ActivityTab
                      entityType={isRecurringTemplate ? 'template' : 'instance'}
                      entityId={itemId}
                    />
                  </div>
                ),
              },
              {
                key: 'notes',
                label: (
                  <Space>
                    <FileTextOutlined />
                    <span>Notes & Details</span>
                  </Space>
                ),
                children: (
                  <div style={{ minHeight: 200 }}>
                    <RichTextEditor
                      value={descriptionContent}
                      onChange={handleDescriptionChange}
                      onEditorReady={handleEditorReady}
                      placeholder="Add details, notes, or instructions... (type / for commands)"
                      minHeight={200}
                      fullscreen={isFullScreen}
                      autoFocus={false}
                      readOnly={false}
                      fillParent={false}
                    />
                    {itemId && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                        <AIQuickActions
                          content={descriptionText}
                          onContentReplace={(newContent) => {
                            loadDescription(newContent);
                            debouncedSave(descriptionHtml || newContent);
                          }}
                          onOpenChat={() => setShowChat(true)}
                          disabled={!descriptionText?.trim()}
                        />
                        <Button
                          type="text"
                          size="small"
                          icon={<HistoryOutlined />}
                          onClick={() => setShowVersionHistory(true)}
                          style={{ fontSize: 12 }}
                        />
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'attachments',
                label: (
                  <Space>
                    <PaperClipOutlined />
                    <span>Attachments</span>
                  </Space>
                ),
                children: (
                  <div>
                    <Upload
                      name="file"
                      multiple
                      fileList={fileList}
                      listType="text"
                      action={`${apiClient.defaults.baseURL}/v1/filemanager/upload?entityId=${itemId}`}
                      withCredentials
                      onChange={(info) => {
                        setFileList(info.fileList);
                        const { status } = info.file;
                        if (status === 'done') {
                          const fileName = info.file.response?.name || info.file.name;
                          message.success(`${fileName} uploaded successfully.`);
                          refetchFiles();
                          queryClient.invalidateQueries({ queryKey: ['files'] });
                        } else if (status === 'error') {
                          message.error(`${info.file.name} file upload failed.`);
                        }
                      }}
                      showUploadList={{
                        showRemoveIcon: true,
                        showDownloadIcon: true,
                      }}
                    >
                      <Button icon={<InboxOutlined />} size="small">
                        Upload Files
                      </Button>
                    </Upload>
                    {itemId && (
                      <div style={{ marginTop: 8 }}>
                        <FileList
                          entityType={isRecurringTemplate ? 'template' : 'instance'}
                          entityId={itemId}
                        />
                      </div>
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}

        {/* Notes section for create mode (no collapse) */}
        {mode === 'create' && (
          <div style={{ marginTop: 16, flex: 1, minHeight: 0 }}>
            <div style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
              <FileTextOutlined /> Notes & Details
            </div>
            <RichTextEditor
              value={descriptionContent}
              onChange={handleDescriptionChange}
              onEditorReady={handleEditorReady}
              placeholder="Add details, notes, or instructions... (type / for commands)"
              minHeight={200}
              fullscreen={isFullScreen}
              autoFocus={false}
              readOnly={false}
              fillParent={true}
            />
          </div>
        )}

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: 16,
          borderTop: '1px solid var(--border-default, #e4e7ec)',
          flexShrink: 0,
          marginTop: 16,
        }}>
          <div>
            {itemId && (
              <Popconfirm
                title="Delete this item?"
                description="This action cannot be undone."
                onConfirm={() => isRecurringTemplate ? deleteTemplateMutation.mutate() : deleteInstanceMutation.mutate()}
                okText="Delete"
                okButtonProps={{ danger: true, style: { borderRadius: 2 } }}
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteTemplateMutation.isPending || deleteInstanceMutation.isPending}
                  style={{ borderRadius: 2 }}
                >
                  Delete
                </Button>
              </Popconfirm>
            )}
          </div>
          <Space>
            <span style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, color: 'var(--text-tertiary, #9ca3af)' }}>
              {mode === 'create' ? 'Press Create to save' : 'Changes save automatically'}
            </span>
            {mode === 'create' && (
              <Button
                type="primary"
                onClick={handleCreate}
                loading={isSaving}
                style={{ borderRadius: 2, backgroundColor: 'var(--accent-ink, #1e40af)', borderColor: 'var(--accent-ink, #1e40af)' }}
              >
                Create
              </Button>
            )}
          </Space>
        </div>
      </Form>
    </div>
  );

  // Fullscreen modal
  if (isFullScreen) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width="100%"
        closable={false}
        keyboard={false}
        style={{ top: 0, paddingBottom: 0, maxWidth: '100vw' }}
        styles={{
          body: { height: 'calc(100vh - 48px)', padding: '0 48px' },
          content: { height: '100vh', borderRadius: 0 },
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto', height: '100%', paddingTop: 24, paddingBottom: 24 }}>
          {formContent}
        </div>
      </Modal>
    );
  }

  const entityType = initialIsTemplate ? 'template' : 'instance';

  // Side drawer - 80% width, professional
  return (
    <>
      <Drawer
        placement="right"
        width={Math.min(Math.max(window.innerWidth * 0.8, 700), 1400)}
        onClose={onClose}
        open={open}
        closable={false}
        title={null}
        styles={{
          body: { padding: '0 32px 24px 32px', display: 'flex', flexDirection: 'column' },
        }}
        destroyOnClose
      >
        {formContent}
      </Drawer>

      {/* AI Chat Panel */}
      <AIChatPanel
        open={showChat}
        onClose={() => setShowChat(false)}
        contextContent={descriptionText}
        onPasteToField={(content) => {
          setDescriptionContent(textToYooptaContent(content, true));
          setDescriptionText(content);
          debouncedSave(content);
        }}
      />

      {/* Version History */}
      {itemId && (
        <VersionHistoryDrawer
          open={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
          entityType={entityType}
          entityId={itemId}
          onReverted={(content) => {
            setDescriptionContent(textToYooptaContent(content, true));
            setDescriptionText(content);
            debouncedSave(content);
          }}
        />
      )}
    </>
  );
}
